import { ApiError } from './ApiError';
import { Player, Team, Match, MatchPlayerAbsence, Club, CreateMatchRequest, Formation, FormationPosition, Lineup, LineupSummary, TeamLineupStats, LineupStatsFilter, TeamMatchStats, MatchTypeFilter, PlayerLineupHistory, AnalysisRecording, User, SignupData, TeamInvitation, CreateInvitationRequest, AcceptInvitationRequest, AcceptInvitationResponse, InvitationDetails, HockeyNlClub, HockeyNlTeam, HockeyNlTeamWithMatches, BulkImportMatchesRequest, DashboardOverview, Drill, DrillAgeGroup, DrillPositionInput, DrillVisibility, TeamDeletionReport, TeamAdminOverview, LiveState, LiveSyncOperation, LiveSyncResponse } from '../types/api';
import { Notification, CreateNotificationRequest } from '../types/notifications';
import {
  clearTokens,
  getNativeHeaders,
  getRefreshToken,
  isNative,
  setTokens,
  NATIVE_CLIENT_HEADER,
  NATIVE_CLIENT_VALUE,
} from './nativeAuth';
import {
  clearCache,
  isCacheable,
  notifyFreshData,
  notifyServedFromCache,
  readCache,
  writeCache,
} from './offlineCache';

// Helper function to get configuration values from runtime (window.ENV) or build-time (process.env)
const getConfig = (key: string, defaultValue: string): string => {
  // Check if window.ENV exists (production with runtime config)
  if (typeof window !== 'undefined' && (window as any).ENV) {
    const runtimeValue = (window as any).ENV[key];
    if (runtimeValue) return runtimeValue;
  }

  // Fallback to build-time environment variables (development)
  return process.env[key] || defaultValue;
};

// Use getters to ensure these values are evaluated at runtime, not at module load time
const getApiBaseUrl = () => getConfig('REACT_APP_API_BASE_URL', 'http://localhost:3000');
const getApiTimeout = () => parseInt(getConfig('REACT_APP_API_TIMEOUT', '10000'));
const getDebugApi = () => getConfig('REACT_APP_DEBUG_API', 'false') === 'true';

// Authentication interfaces
export interface AuthResponse {
  access_token: string;
  user: User;
}

export interface AuthError {
  message: string;
  statusCode: number;
}


/**
 * Endpoints waar een 401 iets zegt over de meegestuurde inloggegevens, niet over de sessie.
 *
 * Voor alle andere endpoints betekent 401 "sessie verlopen" en is een token-refresh de juiste
 * reactie. Hier niet: een fout wachtwoord is geen verlopen sessie. Zonder deze uitzondering
 * ging de melding van de backend verloren (elke inlogfout werd `Authentication failed`) en
 * werd een ingelogde gebruiker uitgelogd door een typefout in zijn huidige wachtwoord - of
 * erger, kwam het verzoek in een lus: refresh slaagt, verzoek opnieuw, weer 401, weer refresh.
 *
 * Bewust een opsomming en geen `startsWith('/auth')`: `/auth/me` en
 * `/auth/resend-verification` hebben de refresh-route juist wél nodig.
 */
const CREDENTIAL_ENDPOINTS = [
  '/auth/refresh',
  '/auth/login',
  '/auth/apple',
  '/auth/change-password',
];

class ApiService {
  private async fetchApi<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const url = `${getApiBaseUrl()}${endpoint}`;


    // Create AbortController for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), getApiTimeout());
    
    try {
      // Native heeft geen cookies en stuurt een bearer token mee; op het web is dit leeg.
      const nativeHeaders = await getNativeHeaders();

      // `...options` staat vóór headers en signal: die twee worden hier bewust bepaald
      // en mogen niet door een meegegeven RequestInit overschreven worden. Andersom
      // verloor een aanroeper met eigen `headers` de Content-Type en het bearer token.
      const response = await fetch(url, {
        credentials: 'include', // Always include cookies
        ...options,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          ...options?.headers,
          ...nativeHeaders,
        },
      });
      
      clearTimeout(timeoutId);

      // Handle 401 - attempt token refresh
      if (response.status === 401 && !CREDENTIAL_ENDPOINTS.includes(endpoint)) {
        const refreshed = await this.refreshToken();
        if (refreshed) {
          // Retry original request
          return this.fetchApi(endpoint, options);
        } else {
          // Redirect to login or clear auth state
          this.handleAuthFailure();
          throw new Error('Authentication failed');
        }
      }

      if (!response.ok) {
        // De uitleg uit de body meenemen. De backend zet daar in gewoon Nederlands in
        // wát er misging ("Deze uitnodiging is niet geldig of verlopen"); zonder dit
        // bleef er alleen een statuscode over en toonde elk scherm dezelfde algemene
        // fout, ook waar de code al op de tekst probeerde te reageren.
        const detail = await this.readErrorDetail(response);
        const errorMessage = detail
          ? `API request failed: ${response.status} ${response.statusText} - ${detail}`
          : `API request failed: ${response.status} ${response.statusText}`;
        if (getDebugApi()) {
          console.error('API Error:', errorMessage, url);
        }
        throw new ApiError(errorMessage, response.status, detail ?? undefined);
      }

      // Check if response has content to parse
      const contentType = response.headers.get('Content-Type');
      const hasJsonContent = contentType && contentType.includes('application/json');
      const hasContent = response.status !== 204 && response.status !== 205;
      
      let data;
      if (hasJsonContent && hasContent) {
        data = await response.json();
      } else {
        // For responses without JSON content (like DELETE 204), return undefined
        data = undefined;
      }

      if (isCacheable(endpoint, options?.method)) {
        writeCache(endpoint, data);
        notifyFreshData();
      }

      return data;
    } catch (error) {
      clearTimeout(timeoutId);

      const isNetworkFailure =
        (error instanceof Error && error.name === 'AbortError') || error instanceof TypeError;

      // Alleen terugvallen op de cache als het netwerk het liet afweten. Een 404 of 500
      // is een echt antwoord en moet gewoon een fout blijven, anders verbergt de cache
      // dat er iets stuk is.
      if (isNetworkFailure && isCacheable(endpoint, options?.method)) {
        const cached = readCache<T>(endpoint);
        if (cached) {
          notifyServedFromCache(cached.cachedAt);
          return cached.data;
        }
      }

      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error(`API request timeout after ${getApiTimeout()}ms`);
      }

      // Handle network errors
      if (error instanceof TypeError) {
        throw new Error('Network error: Please check your internet connection and that the backend is running');
      }

      throw error;
    }
  }

  /**
   * Haalt de melding uit een foutantwoord. NestJS stuurt `message` als tekst of, bij
   * validatiefouten, als lijst. Lukt het lezen niet, dan valt de aanroeper terug op
   * alleen de statuscode.
   */
  private async readErrorDetail(response: Response): Promise<string | null> {
    try {
      const body = await response.clone().json();
      const message = body?.message ?? body?.error;
      if (Array.isArray(message)) {
        return message.join(', ');
      }
      return typeof message === 'string' && message.length > 0 ? message : null;
    } catch {
      return null;
    }
  }

  // Dashboard endpoint
  /**
   * Eén verzoek voor het hele coachdashboard: de drie tegels, of er een actieve wedstrijd
   * is, en de recente activiteit. Haal deze cijfers niet opnieuw uit `/matches`,
   * `/lineups` of `/analysis-recordings` - dat waren de zeven verzoeken die dit vervangt,
   * en die lijsten groeien mee met de historie van elk team van de gebruiker.
   */
  async getDashboard(teamId: number): Promise<DashboardOverview> {
    return this.fetchApi<DashboardOverview>(`/dashboard/${teamId}`);
  }

  // Statistics endpoints
  async getTeamLineupStatistics(teamId: number, filter: LineupStatsFilter = {}): Promise<TeamLineupStats> {
    const params = new URLSearchParams();
    if (filter.matchId !== undefined) params.set('matchId', String(filter.matchId));
    if (filter.from) params.set('from', filter.from);
    if (filter.to) params.set('to', filter.to);
    const query = params.toString();
    return this.fetchApi<TeamLineupStats>(`/statistics/teams/${teamId}/lineup${query ? `?${query}` : ''}`);
  }

  async getTeamMatchStatistics(teamId: number, type: MatchTypeFilter = 'alle'): Promise<TeamMatchStats> {
    return this.fetchApi<TeamMatchStats>(`/statistics/teams/${teamId}/matches?type=${type}`);
  }

  async getPlayerLineupHistory(playerId: number, teamId?: number): Promise<PlayerLineupHistory> {
    const params = teamId ? `?teamId=${teamId}` : '';
    return this.fetchApi<PlayerLineupHistory>(`/statistics/players/${playerId}/lineup-history${params}`);
  }

  // Player endpoints
  async getPlayers(): Promise<Player[]> {
    return this.fetchApi<Player[]>('/players');
  }

  async getPlayer(id: number): Promise<Player> {
    return this.fetchApi<Player>(`/players/${id}`);
  }

  async createPlayer(player: Omit<Player, 'id' | 'added' | 'lastEdited'>): Promise<Player> {
    return this.fetchApi<Player>('/players', {
      method: 'POST',
      body: JSON.stringify(player),
    });
  }

  async updatePlayer(id: number, player: Partial<Omit<Player, 'id' | 'added' | 'lastEdited'>>): Promise<Player> {
    return this.fetchApi<Player>(`/players/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(player),
    });
  }

  async deletePlayer(id: number): Promise<void> {
    await this.fetchApi<void>(`/players/${id}`, {
      method: 'DELETE',
    });
  }

  // Team endpoints
  async getTeams(): Promise<Team[]> {
    return this.fetchApi<Team[]>('/teams');
  }

  async getTeam(id: number): Promise<Team> {
    return this.fetchApi<Team>(`/teams/${id}`);
  }

  async getTeamBySlug(slug: string): Promise<Team> {
    return this.fetchApi<Team>(`/teams/slug/${slug}`);
  }

  async getTeamByPublicId(publicId: string, slug: string): Promise<Team> {
    return this.fetchApi<Team>(`/teams/public/${publicId}/${slug}`);
  }

  // --- Support ----------------------------------------------------------------

  async sendSupportMessage(payload: {
    bericht: string;
    naam?: string;
    email?: string;
    onderwerp?: string;
    pagina?: string;
    website?: string;
  }): Promise<void> {
    await this.fetchApi<{ success: boolean }>('/support', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // --- Publieke spelerspagina -------------------------------------------------
  // Deze draaien zonder login. Ze lopen bewust via /public/*, waar elke route
  // aan de publicId uit de deel-link hangt. Gebruik hier nooit /matches of /lineups:
  // die zijn afgeschermd omdat hun id's oplopende integers zijn en dus af te lopen.

  async getPublicMatches(publicId: string, slug: string): Promise<Match[]> {
    return this.fetchApi<Match[]>(`/public/teams/${publicId}/${slug}/matches`);
  }

  async getPublicLineups(publicId: string, slug: string, matchId: number): Promise<Lineup[]> {
    return this.fetchApi<Lineup[]>(
      `/public/teams/${publicId}/${slug}/matches/${matchId}/lineups`
    );
  }

  // De volgpagina van het live verslag. Bewust niet in de offline-cache: een oude stand
  // als live tonen is erger dan geen stand.
  async getPublicLiveState(publicId: string, slug: string, matchId: number): Promise<LiveState> {
    return this.fetchApi<LiveState>(`/public/teams/${publicId}/${slug}/matches/${matchId}/live`);
  }

  async createTeam(team: Omit<Team, 'teamId' | 'added' | 'lastEdited' | 'players' | 'isActive'>): Promise<Team> {
    return this.fetchApi<Team>('/teams', {
      method: 'POST',
      body: JSON.stringify(team),
    });
  }

  async updateTeam(id: number, team: Partial<Omit<Team, 'teamId' | 'added' | 'lastEdited' | 'players'>>): Promise<Team> {
    return this.fetchApi<Team>(`/teams/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(team),
    });
  }

  /**
   * Wat er zou verdwijnen als dit team verwijderd werd; telt alleen, verwijdert niets.
   * Voedt het bevestigingsvenster, zodat de omvang vóór de klik zichtbaar is.
   */
  async getTeamDeletionPreview(id: number): Promise<TeamDeletionReport> {
    return this.fetchApi<TeamDeletionReport>(`/teams/${id}/deletion-preview`);
  }

  /** Supportoverzicht van één team: leden, tellingen en verweesd-status. Alleen voor admins. */
  async getTeamAdminOverview(id: number): Promise<TeamAdminOverview> {
    return this.fetchApi<TeamAdminOverview>(`/teams/${id}/admin-overview`);
  }

  async deleteTeam(id: number): Promise<TeamDeletionReport> {
    return this.fetchApi<TeamDeletionReport>(`/teams/${id}`, {
      method: 'DELETE',
    });
  }

  async getTeamPlayers(teamId: number): Promise<Player[]> {
    const team = await this.getTeam(teamId);
    return team.players || [];
  }

  async getActiveTeamPlayers(teamId: number): Promise<Player[]> {
    const team = await this.getTeam(teamId);
    return (team.players || []).filter(player => player.isActive);
  }

  // Match endpoints
  async getMatches(teamSlug?: string): Promise<Match[]> {
    const url = teamSlug ? `/matches?teamSlug=${encodeURIComponent(teamSlug)}` : '/matches';
    return this.fetchApi<Match[]>(url);
  }

  async getMatch(id: number): Promise<Match> {
    return this.fetchApi<Match>(`/matches/${id}`);
  }

  async createMatch(matchRequest: CreateMatchRequest): Promise<Match> {
    return this.fetchApi<Match>('/matches', {
      method: 'POST',
      body: JSON.stringify(matchRequest),
    });
  }

  async updateMatch(id: number, match: Partial<Omit<Match, 'matchId' | 'createdAt' | 'updatedAt' | 'captain'>>): Promise<Match> {
    return this.fetchApi<Match>(`/matches/${id}`, {
      method: 'PUT',
      body: JSON.stringify(match),
    });
  }

  async setActiveMatch(matchId: number, teamId: number): Promise<Match> {
    // First get all matches to find current active ones for this team
    const allMatches = await this.getMatches();
    const teamMatches = allMatches.filter(m => m.teamId === teamId);
    
    // Deactivate all other matches for this team
    const deactivationPromises = teamMatches
      .filter(m => m.matchId !== matchId && m.isCurrent)
      .map(m => this.updateMatch(m.matchId, { isCurrent: false }));
    
    await Promise.all(deactivationPromises);
    
    // Now activate the target match
    return this.updateMatch(matchId, { isCurrent: true });
  }

  async deleteMatch(id: number): Promise<void> {
    await this.fetchApi<void>(`/matches/${id}`, {
      method: 'DELETE',
    });
  }

  // Match availability endpoints
  //
  // Alleen afmeldingen worden vastgelegd. Ontbreekt een speler in deze lijst, dan is die
  // aanwezig — en geldt verder gewoon het bestaande onderscheid actief/inactief.
  async getMatchAbsences(matchId: number): Promise<MatchPlayerAbsence[]> {
    return this.fetchApi<MatchPlayerAbsence[]>(`/matches/${matchId}/availability`);
  }

  // Live verslag. Alle tijden gaan als tekst met Amsterdamse offset over de lijn; zie
  // utils/amsterdamTime.ts. Bewust niet in de offline-cache: de outbox houdt zijn eigen
  // laatst bekende stand bij, inclusief wat nog niet verstuurd is.
  async getLiveState(matchId: number): Promise<LiveState> {
    return this.fetchApi<LiveState>(`/matches/${matchId}/live`);
  }

  // Een batch uit de outbox. Elke operatie krijgt een eigen uitkomst terug; een geweigerde
  // operatie houdt de rest niet tegen.
  async syncLive(matchId: number, operations: LiveSyncOperation[]): Promise<LiveSyncResponse> {
    return this.fetchApi<LiveSyncResponse>(`/matches/${matchId}/live/sync`, {
      method: 'POST',
      body: JSON.stringify({ operations }),
    });
  }

  // Afwezig zetten voegt een rij toe, aanwezig zetten haalt hem weer weg. Levert 204 op.
  async setMatchPlayerAvailability(
    matchId: number,
    playerId: number,
    isAvailable: boolean,
  ): Promise<void> {
    await this.fetchApi<void>(
      `/matches/${matchId}/availability/${playerId}`,
      {
        method: 'PUT',
        body: JSON.stringify({ isAvailable }),
      },
    );
  }

  // Club endpoints
  async getClubs(): Promise<Club[]> {
    return this.fetchApi<Club[]>('/clubs');
  }

  async getClub(id: number): Promise<Club> {
    return this.fetchApi<Club>(`/clubs/${id}`);
  }

  async createClub(club: Omit<Club, 'clubId' | 'createdAt' | 'updatedAt'>): Promise<Club> {
    return this.fetchApi<Club>('/clubs', {
      method: 'POST',
      body: JSON.stringify(club),
    });
  }

  async updateClub(id: number, club: Partial<Omit<Club, 'clubId' | 'createdAt' | 'updatedAt'>>): Promise<Club> {
    return this.fetchApi<Club>(`/clubs/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(club),
    });
  }

  async updateClubLogo(id: number, logoUrl: string, verified?: boolean): Promise<Club> {
    return this.fetchApi<Club>(`/clubs/${id}/logo`, {
      method: 'PATCH',
      body: JSON.stringify({ logoUrl, verified }),
    });
  }

  async deleteClub(id: number): Promise<void> {
    await this.fetchApi<void>(`/clubs/${id}`, {
      method: 'DELETE',
    });
  }

  async retrieveClubLogo(id: number): Promise<{ logoUrl: string | null }> {
    return this.fetchApi<{ logoUrl: string | null }>(`/clubs/${id}/retrieve-logo`, {
      method: 'POST',
    });
  }

  async searchHockeyWeereltClubs(query: string, limit?: number): Promise<any[]> {
    const params = new URLSearchParams({ q: query });
    if (limit) {
      params.append('limit', limit.toString());
    }
    return this.fetchApi<any[]>(`/clubs/search?${params}`);
  }

  async createClubFromHockeyWeerelt(hockeyWeereltId: string): Promise<Club> {
    return this.fetchApi<Club>(`/clubs/create-from-hockey-weerelt/${hockeyWeereltId}`, {
      method: 'POST',
    });
  }

  // Formation endpoints
  async getFormations(): Promise<Formation[]> {
    return this.fetchApi<Formation[]>('/formations');
  }

  async getFormationsByTeam(teamId: number): Promise<Formation[]> {
    return this.fetchApi<Formation[]>(`/formations?teamId=${teamId}`);
  }

  async getFormation(id: number): Promise<Formation> {
    return this.fetchApi<Formation>(`/formations/${id}`);
  }

  async getFormationByName(name: string): Promise<Formation> {
    return this.fetchApi<Formation>(`/formations/by-name/${name}`);
  }

  async getFormationWithPositions(id: number): Promise<Formation> {
    return this.fetchApi<Formation>(`/formations/${id}/with-positions`);
  }

  /**
   * Naam en omschrijving van een formatie bijwerken. De backend accepteert een
   * gedeeltelijke PATCH, dus alles wat hier niet in staat - de zone-aantallen, de
   * technische naam, de sorteervolgorde - blijft ongemoeid.
   */
  async updateFormation(id: number, formation: {
    displayName?: string;
    description?: string;
  }): Promise<Formation> {
    return this.fetchApi<Formation>(`/formations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(formation),
    });
  }

  async getFormationPositions(formationId: number): Promise<FormationPosition[]> {
    return this.fetchApi<FormationPosition[]>(`/formations/${formationId}/positions`);
  }

  async createFormationPosition(formationId: number, position: {
    positionName: string;
    xPercent: number;
    yPercent: number;
    zone: 'goalkeeper' | 'backs' | 'midfield' | 'front';
    sortOrder?: number;
  }): Promise<FormationPosition> {
    return this.fetchApi<FormationPosition>(`/formations/${formationId}/positions`, {
      method: 'POST',
      body: JSON.stringify(position),
    });
  }

  /**
   * De backend accepteert een gedeeltelijke PATCH, dus stuur alleen wat er verandert.
   * Een sleep op het veld raakt alleen de coördinaten en mag de naam en zone die
   * intussen ergens anders zijn aangepast niet overschrijven.
   */
  async updateFormationPosition(id: number, position: {
    positionName?: string;
    xPercent?: number;
    yPercent?: number;
    zone?: 'goalkeeper' | 'backs' | 'midfield' | 'front';
  }): Promise<FormationPosition> {
    return this.fetchApi<FormationPosition>(`/formations/positions/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(position),
    });
  }

  /**
   * Haalt een positie uit de formatie. Opstellingen die deze positie gebruiken raken dat
   * vakje kwijt; de backend telt in `removedFromLineups` hoeveel dat er waren.
   */
  async deleteFormationPosition(id: number): Promise<{ removedFromLineups: number }> {
    return this.fetchApi<{ removedFromLineups: number }>(`/formations/positions/${id}`, {
      method: 'DELETE',
    });
  }

  // Drill endpoints
  //
  // Bewust zonder context met localStorage-cache zoals FormationsContext: een oefening
  // verandert terwijl je hem tekent, en een lijst van een half uur oud laat dan een
  // verouderde miniatuur zien.
  /** De eigen oefeningen, plus wat de teams waar je in zit met je delen. */
  async getDrills(): Promise<Drill[]> {
    return this.fetchApi<Drill[]>('/drills');
  }

  /** Alles wat andere coaches publiek gedeeld hebben. Voor de bibliotheek, die nog komt. */
  async getPublicDrills(): Promise<Drill[]> {
    return this.fetchApi<Drill[]>('/drills/public');
  }

  async getDrill(id: number): Promise<Drill> {
    return this.fetchApi<Drill>(`/drills/${id}`);
  }

  async createDrill(drill: {
    title: string;
    /** Alleen nodig bij `visibility: 'team'`; de eigenaar komt uit het token. */
    teamId?: number | null;
    visibility?: DrillVisibility;
    learningGoal?: string | null;
    description?: string | null;
    isIndoor?: boolean;
    ageGroups?: DrillAgeGroup[];
    positions?: DrillPositionInput[];
  }): Promise<Drill> {
    return this.fetchApi<Drill>('/drills', {
      method: 'POST',
      body: JSON.stringify(drill),
    });
  }

  /**
   * Gedeeltelijke wijziging, met één uitzondering: staat er een `positions` in, dan
   * vervángt die de hele tekening. Een tekening laat zich niet zinnig als deelwijziging
   * beschrijven, dus de bewerker stuurt altijd alle markeringen mee.
   */
  async updateDrill(id: number, drill: {
    title?: string;
    teamId?: number | null;
    visibility?: DrillVisibility;
    learningGoal?: string | null;
    description?: string | null;
    isIndoor?: boolean;
    /** Een lege lijst wist de doelgroepen; weglaten laat ze staan. */
    ageGroups?: DrillAgeGroup[];
    positions?: DrillPositionInput[];
  }): Promise<Drill> {
    return this.fetchApi<Drill>(`/drills/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(drill),
    });
  }

  async deleteDrill(id: number): Promise<void> {
    return this.fetchApi<void>(`/drills/${id}`, { method: 'DELETE' });
  }

  /** In je favorieten zetten. Nog een keer sturen verandert niets - het is een stand. */
  async favoriteDrill(id: number): Promise<void> {
    return this.fetchApi<void>(`/drills/${id}/favorite`, { method: 'PUT' });
  }

  async unfavoriteDrill(id: number): Promise<void> {
    return this.fetchApi<void>(`/drills/${id}/favorite`, { method: 'DELETE' });
  }

  // Lineup endpoints
  async getLineups(matchId?: number): Promise<Lineup[]> {
    const url = matchId ? `/lineups?matchId=${matchId}` : '/lineups';
    return this.fetchApi<Lineup[]>(url);
  }

  async getLineup(id: number): Promise<Lineup> {
    return this.fetchApi<Lineup>(`/lineups/${id}`);
  }

  // Alle opstellingen van een team in het kort, voor de keuzelijst bij het kopiëren van
  // een opstelling. Zonder posities, want die lijst loopt over een heel seizoen.
  async getLineupSummaries(teamSlug: string): Promise<LineupSummary[]> {
    return this.fetchApi<LineupSummary[]>(`/lineups/summary?teamSlug=${encodeURIComponent(teamSlug)}`);
  }

  async createLineup(lineup: {
    matchId: number;
    formationId: number;
    name: string;
    positions: {
      playerId?: number | null;
      formationPositionId?: number | null;
      color?: string | null;
    }[];
  }): Promise<Lineup> {
    return this.fetchApi<Lineup>('/lineups', {
      method: 'POST',
      body: JSON.stringify(lineup),
    });
  }

  async updateLineup(id: number, lineup: {
    name?: string;
    positions?: {
      playerId?: number | null;
      formationPositionId?: number | null;
      color?: string | null;
    }[];
  }): Promise<Lineup> {
    return this.fetchApi<Lineup>(`/lineups/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(lineup),
    });
  }

  async deleteLineup(id: number): Promise<void> {
    await this.fetchApi<void>(`/lineups/${id}`, {
      method: 'DELETE',
    });
  }

  // Analysis Recording endpoints
  async getAnalysisRecordings(matchId?: number): Promise<AnalysisRecording[]> {
    const url = matchId ? `/analysis-recordings?matchId=${matchId}` : '/analysis-recordings';
    return this.fetchApi<AnalysisRecording[]>(url);
  }

  async getAnalysisRecording(id: number): Promise<AnalysisRecording> {
    return this.fetchApi<AnalysisRecording>(`/analysis-recordings/${id}`);
  }

  async createAnalysisRecording(recording: {
    matchId: number;
    name: string;
    category: 'ball-possession' | 'penalty-corner-attack' | 'penalty-corner-defence' | 'goal-attempts';
    events: Array<{
      id: string;
      category: string;
      type: string;
      zone?: number;
      timestamp: string;
      metadata?: Record<string, any>;
    }>;
  }): Promise<AnalysisRecording> {
    return this.fetchApi<AnalysisRecording>('/analysis-recordings', {
      method: 'POST',
      body: JSON.stringify(recording),
    });
  }

  async updateAnalysisRecording(id: number, recording: {
    name?: string;
    events?: Array<{
      id: string;
      category: string;
      type: string;
      zone?: number;
      timestamp: string;
      metadata?: Record<string, any>;
    }>;
  }): Promise<AnalysisRecording> {
    return this.fetchApi<AnalysisRecording>(`/analysis-recordings/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(recording),
    });
  }

  async deleteAnalysisRecording(id: number): Promise<void> {
    await this.fetchApi<void>(`/analysis-recordings/${id}`, {
      method: 'DELETE',
    });
  }

  // Authentication endpoints
  async register(userData: SignupData): Promise<{ message: string }> {
    return this.fetchApi<{ message: string }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  /**
   * true: nieuw access token. false: de server wijst de sessie af (401/403).
   *
   * Elke andere uitkomst is een storing en geen oordeel over de sessie, en gooit dus.
   * Een backend die even zijn database kwijt is gaf hier eerder `false`, waarna de
   * gebruiker werd uitgelogd en de app op native zelfs het refresh token wiste.
   */
  private async refreshToken(): Promise<boolean> {
    // De refresh cookie is padgebonden aan /auth/refresh; native heeft geen cookie en
    // stuurt het token daarom in de body.
    const storedRefreshToken = await getRefreshToken();

    // Een netwerkfout gooit hier een TypeError; fetchApi valt daarop terug op de cache.
    const response = await fetch(`${getApiBaseUrl()}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(isNative() ? { [NATIVE_CLIENT_HEADER]: NATIVE_CLIENT_VALUE } : {}),
      },
      body: JSON.stringify(storedRefreshToken ? { refreshToken: storedRefreshToken } : {}),
    });

    if (response.status === 401 || response.status === 403) return false;

    if (!response.ok) {
      throw new ApiError(
        `API request failed: ${response.status} ${response.statusText}`,
        response.status,
      );
    }

    if (isNative()) {
      const data = await response.json();
      if (!data?.accessToken) return false;
      await setTokens(data.accessToken, data.refreshToken);
    }

    return true;
  }

  private handleAuthFailure() {
    // Op native is de Keychain de sessie. Blijft een verlopen token staan, dan start de
    // app na een herstart opnieuw op met een token dat niet meer werkt.
    void clearTokens();

    // Clear any client-side auth state and dispatch auth failure event
    window.dispatchEvent(new CustomEvent('auth-failure'));
  }

  async login(email: string, password: string): Promise<AuthResponse> {
    const response = await this.fetchApi<{
      user: any;
      accessToken?: string;
      refreshToken?: string;
    }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    // Native bewaart de tokens in de Keychain. Het web krijgt ze niet in de body:
    // daar blijven de httpOnly cookies de sessie.
    if (isNative() && response.accessToken) {
      await setTokens(response.accessToken, response.refreshToken);
    }

    return { access_token: 'cookie-based', user: response.user };
  }

  /**
   * Wisselt de authorization code van de native Apple-dialoog in voor een sessie. Verder
   * hetzelfde als `login`: native bewaart de tokens in de Keychain, het web houdt cookies.
   */
  async loginWithApple(
    code: string,
    names: { givenName?: string; familyName?: string } = {}
  ): Promise<AuthResponse> {
    const response = await this.fetchApi<{
      user: any;
      accessToken?: string;
      refreshToken?: string;
    }>('/auth/apple', {
      method: 'POST',
      body: JSON.stringify({ code, ...names }),
    });

    if (isNative() && response.accessToken) {
      await setTokens(response.accessToken, response.refreshToken);
    }

    return { access_token: 'cookie-based', user: response.user };
  }

  /** De basis-URL van de API, zodat de webflow er zelf heen kan navigeren. */
  getApiBaseUrl(): string {
    return getApiBaseUrl();
  }

  async forgotPassword(email: string): Promise<{ message: string }> {
    return this.fetchApi<{ message: string }>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    return this.fetchApi<{ message: string }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    });
  }

  async verifyEmail(token: string): Promise<{ message: string }> {
    return this.fetchApi<{ message: string }>(`/auth/verify-email?token=${token}`);
  }

  async resendVerification(): Promise<{ message: string }> {
    return this.fetchApi<{ message: string }>('/auth/resend-verification', {
      method: 'POST',
    });
  }

  async getCurrentUser(): Promise<User> {
    return this.fetchApi<User>('/auth/me');
  }

  logout(): void {
    // Bewust fire-and-forget: AuthContext wist zijn eigen state meteen en wacht niet.
    void this.performLogout().catch(console.error);
  }

  private async performLogout(): Promise<void> {
    // Native heeft geen refresh cookie, dus zonder dit token kan de backend de sessie
    // niet intrekken en blijft die zeven dagen bruikbaar.
    const storedRefreshToken = await getRefreshToken();

    try {
      await this.fetchApi('/auth/logout', {
        method: 'POST',
        body: JSON.stringify(storedRefreshToken ? { refreshToken: storedRefreshToken } : {}),
      });
    } finally {
      // Ook als het verzoek faalt moet lokaal uitloggen lukken. De cache gaat mee: die
      // bevat opstellingen en spelersnamen van het team waar je net uit stapt.
      await clearTokens();
      clearCache();
    }
  }

  // Team membership endpoints
  async getUserTeams(): Promise<any[]> {
    return this.fetchApi<any[]>('/users/me/teams');
  }

  async getTeamMembers(teamId: number): Promise<any[]> {
    return this.fetchApi<any[]>(`/users/teams/${teamId}/members`);
  }

  async addUserToTeam(userId: number, teamId: number, role: string): Promise<any> {
    return this.fetchApi<any>('/users/teams/memberships', {
      method: 'POST',
      body: JSON.stringify({ userId, teamId, role }),
    });
  }

  async removeUserFromTeam(teamId: number, userId: number): Promise<void> {
    await this.fetchApi<void>(`/users/teams/${teamId}/members/${userId}`, {
      method: 'DELETE',
    });
  }

  async updateUserTeamRole(teamId: number, userId: number, role: string): Promise<any> {
    return this.fetchApi<any>(`/users/teams/${teamId}/members/${userId}/role`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    });
  }

  async checkTeamAccess(teamId: number): Promise<{ hasAccess: boolean }> {
    return this.fetchApi<{ hasAccess: boolean }>(`/users/teams/${teamId}/access`);
  }

  // Admin user management endpoints
  async getAllUsers(): Promise<User[]> {
    return this.fetchApi<User[]>('/users');
  }

  async updateUser(userId: number, userData: Partial<Pick<User, 'firstName' | 'lastName' | 'email' | 'role' | 'emailVerified'>>): Promise<User> {
    return this.fetchApi<User>(`/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(userData),
    });
  }

  async updateMyProfile(userData: Partial<Pick<User, 'firstName' | 'lastName'>>): Promise<User> {
    return this.fetchApi<User>('/users/me/profile', {
      method: 'PATCH',
      body: JSON.stringify(userData),
    });
  }

  async deleteUser(userId: number): Promise<void> {
    await this.fetchApi<void>(`/users/${userId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Zegt het eigen account op. De sessie is daarna ongeldig, dus de tokens en de
   * offline-cache moeten weg - de aanroeper logt hierna uit.
   */
  async deleteMyAccount(): Promise<void> {
    await this.fetchApi<void>('/users/me', {
      method: 'DELETE',
    });

    await clearTokens();
    clearCache();
  }

  async updateUserTeamMemberships(userId: number, memberships: { teamId: number; role: string }[]): Promise<void> {
    await this.fetchApi<void>(`/users/${userId}/team-memberships`, {
      method: 'PUT',
      body: JSON.stringify({ memberships }),
    });
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    return this.fetchApi<void>(`/auth/change-password`, {
      method: 'PATCH',
      body: JSON.stringify({ 
        currentPassword, 
        newPassword 
      }),
    });
  }

  // Team invitation endpoints
  async inviteTeamMember(invitationData: CreateInvitationRequest): Promise<TeamInvitation> {
    return this.fetchApi<TeamInvitation>('/invitations', {
      method: 'POST',
      body: JSON.stringify(invitationData),
    });
  }

  async getTeamInvitations(teamId: number): Promise<TeamInvitation[]> {
    return this.fetchApi<TeamInvitation[]>(`/invitations/team/${teamId}`);
  }

  async cancelInvitation(invitationId: number): Promise<{ message: string }> {
    return this.fetchApi<{ message: string }>(`/invitations/${invitationId}`, {
      method: 'DELETE',
    });
  }

  async getInvitationDetails(token: string): Promise<InvitationDetails> {
    return this.fetchApi<InvitationDetails>(`/invitations/details/${encodeURIComponent(token)}`);
  }

  async acceptInvitation(acceptData: AcceptInvitationRequest): Promise<AcceptInvitationResponse> {
    return this.fetchApi<AcceptInvitationResponse>('/invitations/accept', {
      method: 'POST',
      body: JSON.stringify(acceptData),
    });
  }

  // Notification endpoints
  async getNotifications(limit = 10, offset = 0): Promise<{ notifications: Notification[]; total: number; unreadCount: number }> {
    return this.fetchApi<{ notifications: Notification[]; total: number; unreadCount: number }>(`/notifications?limit=${limit}&offset=${offset}`);
  }

  async getUnreadNotificationCount(): Promise<{ count: number }> {
    return this.fetchApi<{ count: number }>('/notifications/unread-count');
  }

  async markNotificationAsRead(notificationId: string): Promise<void> {
    return this.fetchApi<void>(`/notifications/${notificationId}/read`, {
      method: 'PATCH',
    });
  }

  async markAllNotificationsAsRead(): Promise<{ affected: number }> {
    return this.fetchApi<{ affected: number }>('/notifications/mark-all-read', {
      method: 'POST',
    });
  }

  async deleteNotification(notificationId: string): Promise<void> {
    return this.fetchApi<void>(`/notifications/${notificationId}`, {
      method: 'DELETE',
    });
  }

  async createNotification(notificationData: CreateNotificationRequest): Promise<Notification> {
    return this.fetchApi<Notification>('/notifications', {
      method: 'POST',
      body: JSON.stringify(notificationData),
    });
  }

  // Admin-only team notification management
  async createGlobalTeamNotification(notificationData: {
    title: string;
    message: string;
    priority?: 'low' | 'medium' | 'high';
    actionUrl?: string;
  }): Promise<{ created: number; message: string }> {
    return this.fetchApi<{ created: number; message: string }>('/notifications/team-update/global', {
      method: 'POST',
      body: JSON.stringify(notificationData),
    });
  }

  async getAdminTeamNotifications(limit = 20, offset = 0): Promise<{
    notifications: any[];
    total: number;
  }> {
    return this.fetchApi<{ notifications: any[]; total: number }>(`/notifications/team-update/admin?limit=${limit}&offset=${offset}`);
  }

  async deleteTeamNotification(title: string, createdAt: string): Promise<{ deleted: number }> {
    const encodedTitle = encodeURIComponent(title);
    return this.fetchApi<{ deleted: number }>(`/notifications/team-update/${encodedTitle}/${createdAt}`, {
      method: 'DELETE',
    });
  }

  // Hockey NL import endpoints
  async getHockeyNlClubs(search?: string): Promise<HockeyNlClub[]> {
    const url = search ? `/hockey-nl/clubs?search=${encodeURIComponent(search)}` : '/hockey-nl/clubs';
    return this.fetchApi<HockeyNlClub[]>(url);
  }

  async getHockeyNlTeamsByClub(clubId: string): Promise<HockeyNlTeam[]> {
    return this.fetchApi<HockeyNlTeam[]>(`/hockey-nl/clubs/${clubId}/teams`);
  }

  async getHockeyNlTeamDetails(pouleId: number, teamId: number): Promise<HockeyNlTeamWithMatches> {
    return this.fetchApi<HockeyNlTeamWithMatches>(`/hockey-nl/poules/${pouleId}/teams/${teamId}`);
  }

  async bulkImportMatches(request: BulkImportMatchesRequest): Promise<Match[]> {
    return this.fetchApi<Match[]>('/matches/bulk-import', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }
}

export const apiService = new ApiService();